import {
  setAuthCredentials,
  throwErrorMessage,
  clearLocalStorageOnLogout,
} from 'dashboard/store/utils/api';
import wootAPI from './apiClient';
import {
  getLoginRedirectURL,
  getCredentialsFromEmail,
} from '../helpers/AuthHelper';

export const login = async ({
  ssoAccountId,
  ssoConversationId,
  autoRedirect = true,
  ...credentials
}) => {
  try {
    const response = await wootAPI.post('auth/sign_in', credentials);

    // Check if MFA is required
    if (response.status === 206 && response.data.mfa_required) {
      // Return MFA data instead of throwing error
      return {
        mfaRequired: true,
        mfaToken: response.data.mfa_token,
      };
    }

    setAuthCredentials(response);
    clearLocalStorageOnLogout();
    const redirectURL = getLoginRedirectURL({
      ssoAccountId,
      ssoConversationId,
      user: response.data.data,
    });
    // The branded login page sets `autoRedirect: false` so it can show a
    // success screen before navigating. Legacy callers keep the prior
    // auto-redirect behavior.
    if (autoRedirect) {
      window.location = redirectURL;
      return null;
    }
    return { success: true, user: response.data.data, redirectURL };
  } catch (error) {
    // Check if it's an MFA required response
    if (error.response?.status === 206 && error.response?.data?.mfa_required) {
      return {
        mfaRequired: true,
        mfaToken: error.response.data.mfa_token,
      };
    }
    // Admin-driven block — backend tags the response with code +
    // paused_role so the login page routes to the branded dead-end page:
    //   • account_paused    — per-user pause
    //   • account_suspended — whole-agency block
    const code = error.response?.data?.code;
    if (code === 'account_paused' || code === 'account_suspended') {
      return {
        blocked: true,
        reason: code === 'account_suspended' ? 'suspended' : 'paused',
        role: error.response.data.paused_role || 'admin',
      };
    }
    // Devise :lockable surfaces a locked-account message in the error
    // response. devise_token_auth phrases it slightly differently from
    // upstream Devise — match both variants so we work regardless of
    // which gem the host environment is running.
    //   devise_token_auth: "Your account has been locked due to an
    //                       excessive number of unsuccessful sign in
    //                       attempts."
    //   devise (upstream): "Your account is locked."
    // (devise_token_auth does NOT emit a `last_attempt_warning` line —
    // that's only on upstream Devise. We surface remaining-attempts via
    // a client-side counter instead, in Login Index.vue.)
    const message = error.response?.data?.errors?.[0] || error.message || '';
    if (/account.*(?:has been|is) locked|locked due to/i.test(message)) {
      return { locked: true, message };
    }
    if (/one more attempt/i.test(message)) {
      return { lastAttempt: true, message };
    }
    throwErrorMessage(error);
    return null;
  }
};

export const register = async creds => {
  try {
    const derived = getCredentialsFromEmail(creds.email);
    const response = await wootAPI.post('api/v1/accounts.json', {
      account_name: creds.accountName?.trim() || derived.accountName,
      user_full_name: creds.fullName?.trim() || derived.fullName,
      email: creds.email,
      password: creds.password,
      h_captcha_client_response: creds.hCaptchaClientResponse,
    });
    return response.data;
  } catch (error) {
    throwErrorMessage(error);
  }
  return null;
};

export const resendConfirmation = async ({ email, hCaptchaClientResponse }) => {
  return wootAPI.post('resend_confirmation', {
    email,
    h_captcha_client_response: hCaptchaClientResponse,
  });
};

export const verifyPasswordToken = async ({ confirmationToken }) => {
  try {
    const response = await wootAPI.post('auth/confirmation', {
      confirmation_token: confirmationToken,
    });
    setAuthCredentials(response);
    return response.data?.data;
  } catch (error) {
    throwErrorMessage(error);
    return null;
  }
};

export const setNewPassword = async ({
  resetPasswordToken,
  password,
  confirmPassword,
}) => {
  try {
    const response = await wootAPI.put('auth/password', {
      reset_password_token: resetPasswordToken,
      password_confirmation: confirmPassword,
      password,
    });
    setAuthCredentials(response);
  } catch (error) {
    throwErrorMessage(error);
  }
};

export const resetPassword = async ({ email }) =>
  wootAPI.post('auth/password', { email });
